// Instanced mesh managers for enemies, projectiles, and pickups.
// Gate 1 visual classes only; keeps draw calls low.

import * as THREE from 'three';
import type { Enemy, Projectile, Pickup, Vec2 } from '../types.js';
import enemiesJson from '../content/enemies.json';

export type InstanceKind = 'enemy' | 'projectile' | 'pickup';

export class InstanceManager {
  readonly mesh: THREE.InstancedMesh;
  private readonly dummy = new THREE.Object3D();
  private count = 0;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, maxCount: number) {
    this.mesh = new THREE.InstancedMesh(geometry, material, maxCount);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
  }

  reset(): void {
    this.count = 0;
  }

  add(matrix: THREE.Matrix4, color: THREE.Color): void {
    if (this.count >= this.mesh.instanceMatrix.count) return;
    this.mesh.setMatrixAt(this.count, matrix);
    this.mesh.setColorAt(this.count, color);
    this.count++;
  }

  finalize(): void {
    this.mesh.count = this.count;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) {
      this.mesh.instanceColor.needsUpdate = true;
    }
  }
}

function makeConeTeardrop(radius: number, height: number, segments: number): THREE.BufferGeometry {
  const geo = new THREE.ConeGeometry(radius, height, segments);
  // Cone points along +Y; our sprites lie in XY with camera looking down -Z.
  return geo;
}

// Bullet streak: a velocity-aligned comet quad — hot pointed head fading to a
// dim narrow tail — so travel direction reads in a single still frame. +Y is
// forward (projectileMatrix rotates +Y onto velocity). The baked vertex-color
// gradient multiplies the instance hue: the head keeps the full ×1.6 HDR push
// (blooms), the tail drops under the bloom threshold (crisp, no smear).
function makeBulletStreak(): THREE.BufferGeometry {
  const LENGTH = 1.8; // ~6× the 0.3 width
  const HEAD = 0.55; // head tip sits at the hitbox's front edge, tail trails
  const geo = new THREE.PlaneGeometry(0.3, LENGTH, 1, 8);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) + LENGTH / 2) / LENGTH; // 0 tail → 1 head
    // Kite profile: narrow tail, full width at ~62%, sharp point at the tip.
    const w = t <= 0.62 ? 0.3 + 0.7 * (t / 0.62) : 1 - 0.95 * ((t - 0.62) / 0.38);
    pos.setX(i, pos.getX(i) * w);
    // Shift back: head tip lands at +HEAD instead of +LENGTH/2.
    pos.setY(i, pos.getY(i) - (LENGTH / 2 - HEAD));
    const c = 0.22 + 0.78 * t * t;
    colors[i * 3] = c;
    colors[i * 3 + 1] = c;
    colors[i * 3 + 2] = c;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

function makeCoin(radius: number, sides: number): THREE.BufferGeometry {
  // Flat polygonal coin in the XY plane (camera looks down -Z).
  const geo = new THREE.CircleGeometry(radius, sides);
  return geo;
}

function makeSquare(radius: number): THREE.BufferGeometry {
  // Axis-aligned square: a 4-segment circle rotated π/4 so its edges (not
  // points) face the cardinal directions — visually distinct from the diamond.
  const geo = new THREE.CircleGeometry(radius, 4);
  geo.rotateZ(Math.PI / 4);
  return geo;
}

function makeMaterial(color: number, emissiveIntensity: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: color,
    emissiveIntensity,
    roughness: 0.4,
    metalness: 0.1,
    transparent: true,
    depthWrite: false
  });
}

// Unlit neon material for the combatant layers: instanceColor drives the
// fragment directly (no diffuse/emissive split to wash out), so per-instance
// hues survive at full saturation and HDR values (>1) push into UnrealBloom.
function makeNeonMaterial(additive: boolean): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    depthWrite: false,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending
  });
}

// Enemy base is a bright Geometry-Wars cyan: clearly above the background
// noise floor at 200-density, still hue-separated from the warm-gold player.
// Enemy base is an aggressive crimson (hostile read against the warm-gold
// player; accents shift within the red family per archetype).
export const ENEMY_COLOR = new THREE.Color(0xff4456);
export const PROJECTILE_COLOR = new THREE.Color(0xffd97a);
// Faction routing for projectiles: ally fire keeps the classic gold. Enemy
// bullets (boss volleys, spitter lobs) are white-hot pink — they leave the
// red hull family so a boss volley parses from the boss silhouette instead
// of fusing with it (hue ~324° + high lightness; the ×1.6 push in
// projectileMatrix blooms the core to near-white).
// projectileMatrix recolors the shared PROJECTILE_COLOR per instance — the
// renderer passes it to add() immediately after each projectileMatrix call.
const ALLY_PROJECTILE_COLOR = new THREE.Color(0xffd97a);
const ENEMY_PROJECTILE_COLOR = new THREE.Color(0xff9ed8);
export const PICKUP_XP_COLOR = new THREE.Color(0x7dff4f);
// Health drops read as warm gold (value pickup), not hostile red.
export const PICKUP_HP_COLOR = new THREE.Color(0xffd36b);
// Bright near-white mint ring drawn around the round coin pickups so they
// read as value items even against the green dartlings.
export const PICKUP_RIM_COLOR = new THREE.Color(0xeafff5);

const WHITE = new THREE.Color(0xffffff);

// Per-role neon hue families, mapped render-side (content hues in
// enemies.json stay untouched). Keeps the red-aggressive identity but splits
// by role so every archetype reads as a distinct threat at a glance.
// Exported for the palette-separation regression test (tests/palette-separation.test.ts).
export const ROLE_HUES = new Map<string, number>([
  ['enemy.driftfin', 0xff2e4a], // basic pursuer: hot red
  ['enemy.bulwark', 0xd1002a], // slow tank: blood crimson (+ white-hot rim)
  ['enemy.dartling', 0xff7300], // fast rusher: searing orange
  ['enemy.weaver', 0xff2ec4], // evasive strafer: hot magenta
  ['enemy.dodger', 0xff5e7a], // evasive skirmisher: hot pink
  ['enemy.splitter', 0xffd23d], // splits on death: warning yellow
  ['enemy.splitter-mini', 0xffe066], // split spawn: light warning yellow
  ['enemy.spitter', 0xff9e1f], // ranged lobber: ember orange
  ['enemy.void-fiend', 0xc44dff], // bonus-wave hunter: boss purple
  ['enemy.abyss-maw', 0xff2e63], // boss: searing pink-red
  ['enemy.void-razor', 0xb44dff], // boss duelist: violet
  ['enemy.carrier', 0xb3125e], // brood producer: hot violet-red
  ['enemy.leech', 0xff1a72], // latch-on parasite: hot magenta-red
]);

/** Role hue for a def id (trails, death tints — anywhere outside instances). */
export function roleHueFor(defId: string): number | undefined {
  return ROLE_HUES.get(defId);
}

export type EnemySilhouette = 'teardrop' | 'dart' | 'diamond' | 'hexagon' | 'square';

// Enemy defs: render reads silhouette/scale routing from content (hues come
// from ROLE_HUES above, not the content `hue` field).
const ENEMY_DEFS = (enemiesJson as {
  entries: { id: string; hue: string; visual?: { silhouette?: string; scale?: number } }[];
}).entries;

// Content spells new defs with the short enum ('dart', 'hexagon', ...); older
// descriptive strings ('plump-teardrop-tail-fin') and anything unrecognized
// fall back to the teardrop cone so unknown defs still render.
function parseSilhouette(raw: string | undefined): EnemySilhouette {
  switch (raw) {
    case 'dart':
    case 'diamond':
    case 'hexagon':
    case 'square':
      return raw;
    default:
      return 'teardrop';
  }
}

// def_id → silhouette routing, precomputed once at module load so the
// per-frame enemy path is an O(1) lookup with zero allocation.
const ENEMY_SILHOUETTES = new Map<string, EnemySilhouette>();
for (const def of ENEMY_DEFS) {
  ENEMY_SILHOUETTES.set(def.id, parseSilhouette(def.visual?.silhouette));
}

// def_id → content visual scale (archetype bulk: slim dartling 0.9 … heavy
// bulwark 1.9). Bosses ignore it — their 3.2 multiplier is the boss read.
const DEF_SCALES = new Map<string, number>();
for (const def of ENEMY_DEFS) {
  DEF_SCALES.set(def.id, def.visual?.scale ?? 1);
}

/** Shared base scale for an enemy (boss/elite × def scale × per-instance
 * variation × depth plane), used by the silhouette and its damage chips. */
function enemyBaseScale(e: Enemy): number {
  const vary = 0.85 + hash01(e.pool_index * 3 + 13) * 0.3;
  let scale = (e.boss ? 3.2 : e.elite ? 1.5 : 1.0) * (DEF_SCALES.get(e.def_id) ?? 1) * vary;
  scale *= e.plane === 2 ? 1.12 : e.plane === 0 ? 0.82 : 1.0;
  return scale;
}

// Deterministic 0..1 hash from an integer (pool_index); no Math.random so
// per-instance variation is stable across identical sim runs.
function hash01(n: number): number {
  let h = Math.imul(n | 0, 2654435761);
  h = Math.imul(h ^ (h >>> 15), 2246822519);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

// Per-enemy neon color: the role hue family with a tight jitter so swarm
// members read as individuals without losing the role's color identity.
export function enemyColor(e: Enemy, out: THREE.Color): THREE.Color {
  const h1 = hash01(e.pool_index * 3 + 11);
  const h2 = hash01(e.pool_index * 3 + 12);
  out.set(ROLE_HUES.get(e.def_id) ?? ENEMY_COLOR.getHex());
  // Aggression: push saturation hard, then a narrow hue/brightness spread so
  // the role hue still survives at 200-density.
  out.offsetHSL((h1 - 0.5) * 0.04, 0.14, (h2 - 0.5) * 0.16);
  // Virtual depth: near plane is brighter, deep plane is darker.
  const planeBrightness = e.plane === 2 ? 1.12 : e.plane === 0 ? 0.62 : 1.0;
  out.multiplyScalar(planeBrightness);
  // Elites read as champions at a glance: a white-hot tint lift. Bosses keep
  // most of their hue (a hot body stages better than a pale one).
  if (e.elite) {
    out.lerp(WHITE, e.boss !== undefined ? 0.15 : 0.35);
    out.multiplyScalar(e.boss !== undefined ? 1.1 : 1.15);
  }
  return out;
}

// Hot rim underlay color: the role hue lifted toward white and pushed past
// 1.0 (HDR) so UnrealBloom engages on the silhouette EDGE instead of the
// body — every enemy gets a bloom rim without the frame clipping to white at
// 200-enemy density. Bulwarks (tanks) get the near-pure-white rim. The white
// lift is tuned so the post-rolloff rim lands above the 0.6 bloom threshold
// (≥0.53 Rec.601 luma even for the darkest hues) while body fills stay below
// it — see the luminance budget table in post-process.ts.
export function enemyRimColor(e: Enemy, out: THREE.Color): THREE.Color {
  enemyColor(e, out);
  // Boss rim goes near-white hot: its edge must cut through the boss's own
  // bullet cloud, and the pale edge reads against the saturated body hue.
  const whiteLift = e.boss ? 0.68 : e.def_id === 'enemy.bulwark' ? 0.65 : e.elite ? 0.6 : 0.55;
  out.lerp(WHITE, whiteLift);
  // Boss rims push harder into HDR (×1.55) so bloom halos the outline; the
  // body keeps its hue, so the bright edge reads as a hot outline, not a wash.
  out.multiplyScalar(e.boss ? 1.55 : 1.3);
  return out;
}

const SILHOUETTE_KEYS: readonly EnemySilhouette[] = ['teardrop', 'dart', 'diamond', 'hexagon', 'square'];

type PickupShape = 'coin' | 'hex' | 'oct';
const PICKUP_SHAPE_KEYS: readonly PickupShape[] = ['coin', 'hex', 'oct'];

// Procedural shape pick per pickup: mostly round coins, some polygon tokens.
// Deterministic from pool_index so identical runs render identical fragments.
function pickupShape(poolIndex: number): PickupShape {
  const h = hash01(poolIndex * 7 + 1);
  return h < 0.6 ? 'coin' : h < 0.8 ? 'hex' : 'oct';
}

export class InstanceRenderers {
  // One InstancedMesh per enemy silhouette, plus a paired additive rim layer
  // (the bloom-catching hot edge); all live in this group so the scene adds a
  // single node (10 enemy draws: 5 cores + 5 rims).
  readonly enemyGroup: THREE.Group;
  readonly projectiles: InstanceManager;
  private readonly enemyManagers: Record<EnemySilhouette, InstanceManager>;
  private readonly rimManagers: Record<EnemySilhouette, InstanceManager>;
  /** Facet layer: same silhouettes in wireframe — the "texture" pass that
   * makes bodies read as plated constructs instead of flat cutouts. */
  private readonly wireManagers: Record<EnemySilhouette, InstanceManager>;
  // Energy fragments: three procedurally-picked coin shapes plus a bright rim
  // ring on the round coins (3 pickup draws + 1 rim, still ≤ ~12 total).
  readonly pickupGroup: THREE.Group;
  private readonly pickupManagers: Record<PickupShape, InstanceManager>;
  private readonly coinRim: InstanceManager;

  constructor(maxEnemies: number, maxProjectiles: number, maxPickups: number) {
    // All silhouette meshes share one unlit neon material; per-instance hue
    // rides on each mesh's instanceColor buffer and lands on the fragment
    // directly, so role hues stay fully saturated (no diffuse/emissive wash).
    const enemyMaterial = makeNeonMaterial(false);
    // Rim underlay: same silhouettes as OUTLINES (wireframe — never fills the
    // body), additive + HDR colors so the shape EDGES glow hot (the Geometry
    // Wars neon border) without washing the body color out.
    const rimMaterial = makeNeonMaterial(true);
    rimMaterial.wireframe = true;
    const rimManagers = {} as Record<EnemySilhouette, InstanceManager>;
    this.rimManagers = rimManagers;
    this.enemyManagers = {
      // Teardrop: the classic swarm cone (driftfin, void-fiend, abyss-maw).
      teardrop: new InstanceManager(makeConeTeardrop(0.45, 0.9, 6), enemyMaterial, maxEnemies),
      // Dart: slim, elongated cone for fast rushers and the void-razor boss.
      dart: new InstanceManager(makeConeTeardrop(0.28, 1.35, 5), enemyMaterial, maxEnemies),
      // Diamond: rhombus standing on a point (weaver).
      diamond: new InstanceManager(makeCoin(0.5, 4), enemyMaterial, maxEnemies),
      // Hexagon: broad six-sided plate; the bulwark reads as heavy armor.
      hexagon: new InstanceManager(makeCoin(0.55, 6), enemyMaterial, maxEnemies),
      // Square: axis-aligned block (splitter and its minis).
      square: new InstanceManager(makeSquare(0.45), enemyMaterial, maxEnemies),
    };
    this.enemyGroup = new THREE.Group();
    const wireMaterial = makeNeonMaterial(true);
    wireMaterial.wireframe = true;
    this.wireManagers = {} as Record<EnemySilhouette, InstanceManager>;
    for (const key of SILHOUETTE_KEYS) {
      // The rim shares the core's geometry (scaled up per instance at add
      // time) and sits a touch deeper (-0.02 z) so the transparent pass
      // always paints it beneath its core.
      const core = this.enemyManagers[key];
      const rim = new InstanceManager(core.mesh.geometry, rimMaterial, maxEnemies);
      rimManagers[key] = rim;
      this.enemyGroup.add(rim.mesh);
      this.enemyGroup.add(core.mesh);
      // Facet wireframe rides the same geometry, scaled down at add time.
      const wire = new InstanceManager(core.mesh.geometry, wireMaterial, maxEnemies);
      this.wireManagers[key] = wire;
      this.enemyGroup.add(wire.mesh);
    }
    this.projectiles = new InstanceManager(
      makeBulletStreak(),
      (() => {
        // Vertex colors carry the streak's head→tail gradient; instanceColor
        // (faction hue × HDR) multiplies on top per bullet.
        const m = makeNeonMaterial(false);
        m.vertexColors = true;
        return m;
      })(),
      maxProjectiles
    );
    const pickupMaterial = makeNeonMaterial(false);
    this.pickupManagers = {
      // Round coin (24-gon) with the rim ring: the classic "value" read.
      coin: new InstanceManager(makeCoin(0.24, 24), pickupMaterial, maxPickups),
      // Hexagonal and octagonal token variants, picked per pool_index hash.
      hex: new InstanceManager(makeCoin(0.32, 6), pickupMaterial, maxPickups),
      oct: new InstanceManager(makeCoin(0.32, 8), pickupMaterial, maxPickups),
    };
    this.coinRim = new InstanceManager(
      new THREE.RingGeometry(0.24, 0.35, 24),
      makeNeonMaterial(true),
      maxPickups
    );
    this.pickupGroup = new THREE.Group();
    for (const key of PICKUP_SHAPE_KEYS) {
      this.pickupGroup.add(this.pickupManagers[key].mesh);
    }
    this.pickupGroup.add(this.coinRim.mesh);
  }

  // Routed enemy add: picks the silhouette InstancedMesh for the enemy's def
  // (teardrop fallback for unknown ids), dims the body so the bright facet
  // wireframe and the pulsing rim carry the read (GW: dark body, hot edges).
  // Body dim ×0.45 keeps the fill's Rec.601 luma ≤0.47 — below the 0.6 bloom
  // threshold, so body fills never bloom and clumps resolve per-unit (budget
  // table in post-process.ts). O(1), allocation-free.
  addEnemy(e: Enemy, matrix: THREE.Matrix4, color: THREE.Color, time: number): void {
    const silhouette = ENEMY_SILHOUETTES.get(e.def_id) ?? 'teardrop';
    // Dimmer body: the facet lines and rim pop against it instead of washing
    // the whole silhouette into a bright pancake.
    bodyColorScratch.copy(color).multiplyScalar(0.45);
    this.enemyManagers[silhouette].add(matrix, bodyColorScratch);
    // Facet pass: a smaller, brighter wireframe twin — the plating texture.
    rimMatrixScratch.copy(matrix).multiply(WIRE_TRANSFORM);
    wireColorScratch.copy(color).lerp(WHITE, 0.35).multiplyScalar(1.1);
    this.wireManagers[silhouette].add(rimMatrixScratch, wireColorScratch);
    // Breathing border: the rim swells/shrinks ~7% per enemy phase. Bosses
    // get the wider-standoff rim so the outline clears the boss silhouette.
    const pulse = 1 + 0.07 * Math.sin(time * 2.4 + e.pool_index * 1.7);
    rimMatrixScratch.copy(matrix).multiply(e.boss ? BOSS_RIM_TRANSFORM : RIM_TRANSFORM);
    rimMatrixScratch.scale(pulseVecScratch.set(pulse, pulse, 1));
    this.rimManagers[silhouette].add(rimMatrixScratch, enemyRimColor(e, rimColorScratch));
  }

  // Routed pickup add: the pool_index hash picks the coin shape; round coins
  // also draw their bright rim with the same matrix. O(1), allocation-free.
  addPickup(p: Pickup, matrix: THREE.Matrix4, color: THREE.Color, time: number): void {
    const shape = pickupShape(p.pool_index);
    this.pickupManagers[shape].add(matrix, color);
    if (shape === 'coin') {
      // The rim ring shimmers in lockstep with the core's brightness pulse;
      // a hair of extra z keeps the additive ring off its own core.
      const pulse = 1.5 + Math.sin(time * 5 + p.pool_index * 1.31) * 0.4;
      rimMatrixScratch.copy(matrix).multiply(PICKUP_RIM_Z_BUMP);
      this.coinRim.add(rimMatrixScratch, rimColorScratch.copy(PICKUP_RIM_COLOR).multiplyScalar(pulse));
    }
  }

  reset(): void {
    for (const key of SILHOUETTE_KEYS) {
      this.enemyManagers[key].reset();
      this.rimManagers[key].reset();
      this.wireManagers[key].reset();
    }
    this.projectiles.reset();
    for (const key of PICKUP_SHAPE_KEYS) this.pickupManagers[key].reset();
    this.coinRim.reset();
  }

  finalize(): void {
    for (const key of SILHOUETTE_KEYS) {
      this.enemyManagers[key].finalize();
      this.rimManagers[key].finalize();
      this.wireManagers[key].finalize();
    }
    this.projectiles.finalize();
    for (const key of PICKUP_SHAPE_KEYS) this.pickupManagers[key].finalize();
    this.coinRim.finalize();
  }
}

// Shared dummy for matrix composition; avoids per-instance allocation in the
// per-frame hot path. All motion below is visual-only (z-bob, wobble, scale)
// and never touches sim hitboxes.
const matrixDummy = new THREE.Object3D();

// Rim underlay scratches: the hot edge is the core matrix grown ~28% and
// nudged 0.02 deeper so the additive rim bleeds past the silhouette on every
// side while always sorting beneath its core in the transparent pass.
const RIM_TRANSFORM = new THREE.Matrix4()
  .makeTranslation(0, 0, -0.02)
  .multiply(new THREE.Matrix4().makeScale(1.28, 1.28, 1));
/** Boss rim: wider standoff (~40%) so the hot outline clears the boss body
 * and stays legible inside the boss's own bullet cloud. */
const BOSS_RIM_TRANSFORM = new THREE.Matrix4()
  .makeTranslation(0, 0, -0.03)
  .multiply(new THREE.Matrix4().makeScale(1.4, 1.4, 1));
/** Facet layer: the same silhouette shrunk to ~62% and nudged forward. */
const WIRE_TRANSFORM = new THREE.Matrix4()
  .makeTranslation(0, 0, 0.01)
  .multiply(new THREE.Matrix4().makeScale(0.62, 0.62, 1));
const rimMatrixScratch = new THREE.Matrix4();
const rimColorScratch = new THREE.Color();
const wireColorScratch = new THREE.Color();
const bodyColorScratch = new THREE.Color();
const pulseVecScratch = new THREE.Vector3();
// Tiny z bump so the additive pickup rim never ties its own core in the
// transparent sort.
const PICKUP_RIM_Z_BUMP = new THREE.Matrix4().makeTranslation(0, 0, 0.004);

export function enemyMatrix(e: Enemy, time: number, out: THREE.Matrix4, telegraph = false, renderPos?: Vec2): void {
  const phase = e.pool_index * 1.7;
  // Small vertical bob so swarm members drift out of phase with each other.
  const bob = Math.sin(time * 2.1 + phase) * 0.05;
  const px = renderPos ? renderPos.x : e.pos.x;
  const py = renderPos ? renderPos.y : e.pos.y;
  // Virtual depth plane: slight z offset so near/deep layers parallax subtly.
  const planeZ = (e.plane - 1) * 0.03;
  matrixDummy.position.set(px, py, bob + planeZ);
  // Gentle rotational wobble layered on the velocity heading.
  const wobble = Math.sin(time * 1.3 + e.pool_index * 2.3) * 0.08;
  const angle = e.vel.x === 0 && e.vel.y === 0
    ? time * 2 + wobble
    : Math.atan2(e.vel.y, e.vel.x) - Math.PI / 2 + wobble;
  matrixDummy.rotation.z = angle;
  // Base scale shared with the damage chips (boss/elite × def scale ×
  // per-instance variation × depth plane).
  let scale = enemyBaseScale(e);
  if (telegraph) {
    // Spawn telegraph: fast, large pulse so incoming enemies read clearly.
    scale *= 1.15 + Math.sin(time * 10 + phase) * 0.2;
  }
  matrixDummy.scale.set(scale, scale, scale);
  matrixDummy.updateMatrix();
  out.copy(matrixDummy.matrix);
}

export function projectileMatrix(p: Projectile, out: THREE.Matrix4, renderPos?: Vec2): void {
  // z above every enemy layer (max enemy z ≈ 0.08) so incoming fire and ally
  // shots always read over the swarm.
  matrixDummy.position.set(renderPos ? renderPos.x : p.pos.x, renderPos ? renderPos.y : p.pos.y, 0.095);
  const angle = Math.atan2(p.vel.y, p.vel.x) - Math.PI / 2;
  matrixDummy.rotation.z = angle;
  matrixDummy.scale.set(1, 1, 1);
  matrixDummy.updateMatrix();
  out.copy(matrixDummy.matrix);
  // Faction color routing: the shared PROJECTILE_COLOR is consumed by add()
  // right after this call, so recolor it per projectile here.
  PROJECTILE_COLOR.copy(p.faction === 'enemy' ? ENEMY_PROJECTILE_COLOR : ALLY_PROJECTILE_COLOR)
    .multiplyScalar(1.6);
}

export function pickupMatrix(p: Pickup, time: number, out: THREE.Matrix4, renderPos?: Vec2): void {
  // z above every enemy layer (max enemy z ≈ 0.08): drops never hide under
  // the swarm.
  matrixDummy.position.set(renderPos ? renderPos.x : p.pos.x, renderPos ? renderPos.y : p.pos.y, 0.09);
  // Coin read: slow in-plane spin plus a strong edge-on flip (scaleX sweeps
  // toward zero) — the flip is the instant "coin, not enemy" tell, and it
  // works for the polygon token variants too.
  const rotOffset = hash01(p.pool_index * 7 + 3) * Math.PI * 2;
  const scaleJitter = 0.85 + hash01(p.pool_index * 7 + 5) * 0.3;
  matrixDummy.rotation.z = rotOffset + time * 0.6 + p.pool_index * 0.7;
  const pulse = 1 + Math.sin(time * 6 + p.pool_index) * 0.08;
  const flip = 0.12 + 0.88 * Math.abs(Math.cos(time * 2.2 + p.pool_index * 1.1));
  const s = scaleJitter * pulse;
  matrixDummy.scale.set(s * flip, s, 1);
  matrixDummy.updateMatrix();
  out.copy(matrixDummy.matrix);
}

// Pickup tint: saturated mint (xp) / gold (health), pushed into bloom range
// with a shimmer pulse so drops pop against the swarm. Returns a shared
// scratch color — add() copies it into the instance buffer immediately.
const pickupColorScratch = new THREE.Color();
export function pickupColor(p: Pickup, time: number): THREE.Color {
  const pulse = 1.3 + Math.sin(time * 5 + p.pool_index * 1.31) * 0.3;
  return pickupColorScratch
    .copy(p.pickup_kind === 'health' ? PICKUP_HP_COLOR : PICKUP_XP_COLOR)
    .multiplyScalar(pulse);
}
